import iconsManifest from "./iconsManifest.json";
import {
    getSimulatorCategoryKeys,
    mapFolderToDisplayCategory,
    SIMULATOR_EXCLUDED_FOLDERS,
} from "./iconCatalogConfig";
import { VIP_ITEM_PRICES } from "./vipItemPrices";
import { VIP_PRICE_KEY_ALIASES } from "./vipPriceKeyAliases";
import {
    buildCatalogIconCandidates,
    iconAssetUrl,
    isItensDeArmasPath,
    toPublicItensDeArmasPath,
} from "./iconAssets";
import { getAttachmentSpec, isItensDeArmasAccessoryPath } from "./weaponAttachments";
import { getWeaponSpec, isWeaponCatalogFile } from "./weaponSpecs";

const PRICE_KEYS = Object.keys(VIP_ITEM_PRICES);

export { SIMULATOR_EXCLUDED_FOLDERS } from "./iconCatalogConfig";

function catalogId(folder, file) {
    return `${folder}--${String(file).replace(/\\/g, "/")}`;
}

/** Caminho relativo do ícone dentro da pasta (igual ao manifest / public). */
function catalogIconRelPath(folder, sourcePath) {
    const sp = String(sourcePath).replace(/\\/g, "/");
    if (folder === "Armas" && isItensDeArmasPath(sp)) {
        return toPublicItensDeArmasPath(sp);
    }
    return sp;
}

function labelFromFile(file) {
    return file
        .replace(/\.(webp|png|jpe?g|gif|avif)$/i, "")
        .replace(/_icon/gi, "")
        .replace(/\./g, " ")
        .replace(/_/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function normalizePriceAliasKey(s) {
    return s
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/_icon$/i, "")
        .replace(/[._-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function inferPriceKey(file) {
    const base = file.replace(/\.[^.]+$/i, "");
    const candidates = [
        base,
        base.replace(/_icon$/i, ""),
        base.replace(/-/g, " "),
        base.replace(/_icon$/i, "").replace(/-/g, " "),
    ];
    for (const c of candidates) {
        if (Object.prototype.hasOwnProperty.call(VIP_ITEM_PRICES, c)) return c;
        const alias = VIP_PRICE_KEY_ALIASES[normalizePriceAliasKey(c)];
        if (alias && Object.prototype.hasOwnProperty.call(VIP_ITEM_PRICES, alias)) return alias;
    }
    const lower = base.toLowerCase();
    for (const k of PRICE_KEYS) {
        const kl = k.toLowerCase();
        if (kl === lower || kl === lower.replace(/_icon$/, "")) return k;
    }
    const fallback = VIP_PRICE_KEY_ALIASES[normalizePriceAliasKey(candidates[1] || base)];
    if (fallback && Object.prototype.hasOwnProperty.call(VIP_ITEM_PRICES, fallback)) return fallback;
    return candidates[1] || base;
}

function inferPlacementCat(folder, file) {
    const base = file.replace(/\.[^.]+$/i, "");

    if (folder === "Armas") {
        if (getAttachmentSpec(file)) return "attachment";
        if (getWeaponSpec(file)) return "weapon";
        if (/granad|grenade|c4|explosive|flare|smoke/i.test(base)) return "utility";
        if (/club|knife|sword|spear|mace|machete|cleaver|longsword|longsword/i.test(base)) return "tool";
        return "utility";
    }
    if (folder === "Munição") return "ammo";
    if (folder === "Médico" || folder === "Medicamentos" || folder === "Comida") return "consumable";
    if (folder === "Ferramentas") return "tool";

    if (folder === "Roupas") {
        if (/^traje/i.test(base)) return "outfit";
        if (/sela/i.test(base)) return "utility";
        if (/boot|shoe|feet|bota/i.test(base)) return "armor-feet";
        if (/pants|trousers|jeans|legs|calça|calca/i.test(base)) return "armor-legs";
        if (/glove|hands|luva/i.test(base)) return "armor-hands";
        if (/facemask|_mask|máscara|mascara|bandana|balaclava|óculos|oculos|goggles/i.test(base)) {
            return "armor-mask";
        }
        if (/helmet|_hat|headwrap|beenies|cap|headdress|capacete|chapéu|chapeu|cocar/i.test(base)) {
            return "armor-head";
        }
        if (/kilt|belt|skirt|saia/i.test(base)) return "armor-belt";
        if (
            /chest|jacket|hoodie|shirt|plate|vest|torso|armor|camisa|camiseta|casaco|jaqueta|colete|peitoral|blusão|blusao|mergulho/i.test(
                base,
            )
        ) {
            return "armor-chest";
        }
        return "armor-chest";
    }

    return "utility";
}

function quantityDefaults(cat, folder, weaponSpec) {
    const highVolume = folder === "Recursos";

    if (cat.startsWith("armor-") || cat === "outfit") {
        return { quantity: 1, quantityEnabled: false };
    }
    if (cat === "weapon" && weaponSpec) {
        return {
            quantity: 1,
            quantityEnabled: true,
            step: 1,
            min: 1,
            max: weaponSpec.max,
        };
    }
    if (cat === "attachment") {
        return { quantity: 1, quantityEnabled: false };
    }
    if (cat === "weapon") {
        return { quantity: 1, quantityEnabled: true, step: 1, min: 1, max: 2 };
    }
    if (cat === "ammo") {
        return { quantity: 100, quantityEnabled: true, step: 1, min: 1, max: 100 };
    }
    if (cat === "consumable") {
        return { quantity: 10, quantityEnabled: true, step: 5, min: 1, max: 100 };
    }
    if (cat === "tool") {
        return { quantity: 1, quantityEnabled: true, step: 1, min: 1, max: 100 };
    }
    return highVolume
        ? { quantity: 100, quantityEnabled: true, step: 100, min: 1, max: 1000000 }
        : { quantity: 1, quantityEnabled: true, step: 1, min: 1, max: 100 };
}

/**
 * @param {string} folder
 * @param {string} file
 */
export function buildCatalogEntry(folder, file) {
    const displayCategory = mapFolderToDisplayCategory(folder);
    if (!displayCategory) return null;

    const sourcePath = String(file).replace(/\\/g, "/");
    const subAttach = folder === "Armas" && isItensDeArmasAccessoryPath(sourcePath);
    const iconFile = catalogIconRelPath(folder, sourcePath);
    const lookupFile = sourcePath;
    const weaponSpec = folder === "Armas" ? getWeaponSpec(lookupFile) : null;
    const attachSpec = folder === "Armas" ? getAttachmentSpec(lookupFile) : null;
    const cat = inferPlacementCat(folder, lookupFile);
    const qty = quantityDefaults(cat, folder, weaponSpec);
    const baseName = (iconFile.split(/[/\\]/).pop() || iconFile);

    const icon = iconAssetUrl(folder, iconFile);
    const iconCandidates = buildCatalogIconCandidates(folder, iconFile, sourcePath, subAttach).filter(
        (url) => url && url !== icon,
    );

    const entry = {
        id: catalogId(folder, sourcePath),
        name: weaponSpec?.name ?? attachSpec?.name ?? labelFromFile(baseName),
        cat,
        category: displayCategory,
        iconFolder: folder,
        iconFile,
        manifestFile: sourcePath,
        icon,
        iconCandidates,
        priceKey: weaponSpec?.priceKey ?? attachSpec?.priceKey ?? inferPriceKey(baseName),
        ...qty,
    };

    if (weaponSpec) {
        entry.weaponKey = weaponSpec.key;
    }
    if (attachSpec) {
        entry.attachmentSlot = attachSpec.slot;
        entry.compatibleWeapons = attachSpec.weapons;
    }

    return entry;
}

/** Lista de ficheiros Armas/ do manifest — um item por ficheiro (sem fundir duplicados). */
function armasCatalogFiles(files) {
    return files.map((f) => String(f).replace(/\\/g, "/")).sort((a, b) => a.localeCompare(b, "pt"));
}

export function buildCatalogFromManifest() {
    const entries = [];
    const seenIds = new Set();
    for (const { name, files } of iconsManifest.folders) {
        if (SIMULATOR_EXCLUDED_FOLDERS.has(name)) continue;
        const list = name === "Armas" ? armasCatalogFiles(files) : files;
        for (const file of list) {
            if (!isWeaponCatalogFile(name, file)) continue;
            const entry = buildCatalogEntry(name, file);
            if (!entry) continue;
            if (seenIds.has(entry.id)) continue;
            seenIds.add(entry.id);
            entries.push(entry);
        }
    }
    return entries;
}

export function getManifestFolderNames() {
    return getSimulatorCategoryKeys();
}
